import { motion, useMotionValue, useTransform, useSpring } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { GripVertical, Trash2, Globe, Leaf, ExternalLink, Image } from "lucide-react";
import { CHANNELS, STAGE_COLORS, STAGE_LABELS } from "./constants";
import type { Campaign } from "./types";

interface Props {
  campaign: Campaign;
  onDrop: (id: string, channel: string) => void;
  onDelete: (id: string) => void;
}

export function DraggableCampaignCard({ campaign, onDrop, onDelete }: Props) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 300, damping: 20 });
  const springY = useSpring(y, { stiffness: 300, damping: 20 });
  const scale = useTransform(
    [springX, springY],
    ([latestX, latestY]: number[]) => {
      const dist = Math.sqrt(latestX * latestX + latestY * latestY);
      return 1 + Math.min(dist / 800, 0.08);
    }
  );
  const rotate = useTransform(springX, [-200, 200], [-3, 3]);
  const ch = CHANNELS.find((c) => c.id === campaign.channel);
  const Icon = ch?.icon || Globe;

  return (
    <motion.div
      drag
      dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
      dragElastic={0.7}
      dragTransition={{ bounceStiffness: 400, bounceDamping: 15 }}
      style={{ x: springX, y: springY, scale, rotate }}
      whileDrag={{ zIndex: 50, boxShadow: "0 20px 60px -10px rgba(0,0,0,0.5)" }}
      onDragEnd={(_, info) => {
        const els = document.elementsFromPoint(info.point.x, info.point.y);
        const dropTarget = els.find((el) => el.hasAttribute("data-channel-drop"));
        if (dropTarget) {
          const channelId = dropTarget.getAttribute("data-channel-drop")!;
          onDrop(campaign.id, channelId);
        }
      }}
      className="cursor-grab active:cursor-grabbing"
    >
      <Card className="p-3 bg-card/80 backdrop-blur border-border/50 hover:border-primary/30 transition-colors group">
        <div className="flex items-center gap-2 mb-2">
          <GripVertical className="h-3.5 w-3.5 text-muted-foreground/40 group-hover:text-muted-foreground" />
          <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: ch?.color + "22" }}>
            <Icon className="h-3.5 w-3.5" style={{ color: ch?.color }} />
          </div>
          <span className="text-xs font-medium truncate flex-1">{campaign.name}</span>
          {campaign.isOrganic && (
            <span title="Organique"><Leaf className="h-3 w-3 text-emerald-400" /></span>
          )}
          <button onClick={() => onDelete(campaign.id)} className="opacity-0 group-hover:opacity-100 transition-opacity">
            <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
          </button>
        </div>

        {campaign.imageUrl && (
          <div className="mb-2 rounded-md overflow-hidden h-16">
            <img src={campaign.imageUrl} alt="" className="w-full h-full object-cover" />
          </div>
        )}

        <div className="flex items-center justify-between text-[10px] text-muted-foreground">
          <span>{campaign.isOrganic ? "🌱 Organique" : campaign.budget > 0 ? `${campaign.spent}€ / ${campaign.budget}€` : "Gratuit"}</span>
          <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${STAGE_COLORS[campaign.status]}`}>
            {STAGE_LABELS[campaign.status]}
          </Badge>
        </div>

        {campaign.landingUrl && (
          <a
            href={campaign.landingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-[9px] text-primary/70 hover:text-primary mt-1 truncate"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="h-2.5 w-2.5 shrink-0" />
            <span className="truncate">{campaign.landingUrl}</span>
          </a>
        )}

        {campaign.budget > 0 && !campaign.isOrganic && (
          <div className="mt-1.5 h-1 rounded-full bg-muted overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-primary"
              initial={{ width: 0 }}
              animate={{ width: `${Math.min((campaign.spent / campaign.budget) * 100, 100)}%` }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            />
          </div>
        )}

        {/* Mini stats */}
        {(campaign.clicks > 0 || campaign.conversions > 0) && (
          <div className="flex gap-2 mt-2 text-[9px] text-muted-foreground/70">
            {campaign.clicks > 0 && <span>{campaign.clicks} clics</span>}
            {campaign.cpc > 0 && <span>CPC: {campaign.cpc.toFixed(2)}€</span>}
            {campaign.conversions > 0 && <span>{campaign.conversions} conv.</span>}
          </div>
        )}
      </Card>
    </motion.div>
  );
}
